import { isValid, parseISO } from 'date-fns';
import { AppLanguage, getAppLanguage } from './i18n';

const intlLocales: Record<AppLanguage, string> = {
  en: 'en-SE',
  sv: 'sv-SE',
};

function locale(language?: string) {
  return getAppLanguage(language);
}

function parseDate(value?: string | Date | null): Date | null {
  if (!value) return null;
  const parsed = value instanceof Date ? value : parseISO(value);
  return isValid(parsed) ? parsed : null;
}

export function formatDate(value?: string | Date | null, language?: string) {
  const parsed = parseDate(value);
  return parsed
    ? new Intl.DateTimeFormat(intlLocales[locale(language)], { dateStyle: 'medium' }).format(parsed)
    : null;
}

export function formatDateTime(value?: string | Date | null, language?: string) {
  const parsed = parseDate(value);
  return parsed
    ? new Intl.DateTimeFormat(intlLocales[locale(language)], {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(parsed)
    : null;
}

export function formatRelativeTime(value?: string | Date | null, language?: string) {
  const parsed = parseDate(value);
  const appLanguage = locale(language);
  if (!parsed) return null;

  const seconds = (parsed.getTime() - Date.now()) / 1000;
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 365 * 24 * 60 * 60],
    ['month', 30 * 24 * 60 * 60],
    ['week', 7 * 24 * 60 * 60],
    ['day', 24 * 60 * 60],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
  ];
  const [unit, unitSeconds] = units.find(([, size]) => Math.abs(seconds) >= size) ?? units[units.length - 1];
  return new Intl.RelativeTimeFormat(intlLocales[appLanguage], { numeric: 'always' })
    .format(Math.round(seconds / unitSeconds), unit);
}

export function formatNumber(value: number, language?: string, options?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(intlLocales[locale(language)], options).format(value);
}

export function formatCurrency(value: number, language?: string) {
  return formatNumber(value, language, {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  });
}

export function formatPercent(value: number, language?: string, maximumFractionDigits = 1) {
  return formatNumber(value, language, { style: 'percent', maximumFractionDigits });
}

export function formatList(values: string[], language?: string) {
  return new Intl.ListFormat(intlLocales[locale(language)], {
    style: 'long',
    type: 'conjunction',
  }).format(values);
}

export function compareDisplayText(left: string, right: string, language?: string) {
  return new Intl.Collator(intlLocales[locale(language)]).compare(left, right);
}
